import React, { useState } from "react";
import { Alert, Badge, Card, Group, List, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconSparkles } from "@tabler/icons-react";
import AddBonusForm from "../Organization/AddBonusForm";
import AddExpenseForm from "../Organization/AddExpenseForm";
import AddUpfrontPayForm from "../Organization/AddUpfrontPayForm";
import {
  BONUS_CATEGORY_LABELS,
  EXPENSE_CATEGORY_LABELS,
  UPFRONT_PAY_TYPE_LABELS,
  titleize,
} from "./repDealDisplay";
import {
  ItemRow,
  ItemTable,
  ReductionFactorsSection,
  ScaleTable,
  bonusFields,
  expenseFields,
  upfrontFields,
} from "./payEditorPieces";
import { nextEditorItemId } from "../../services/payExtraction";

// The review step of AI pay extraction: what the AI read off a compensation
// document, in the same editable tables as the Send Offer flow's Pay Details
// step, with the document's own wording quoted under each value so it can be
// checked against the source. Everything is a DRAFT until the caller saves —
// this component only edits `state` (see draftToEditorState / payDraftPayload).

const CONFIDENCE_COLOR = { high: "teal", medium: "yellow", low: "red" };

const CONFIDENCE_NOTE = {
  high: "The document was clear. Check the numbers and save.",
  medium: "Some terms were ambiguous. Read every value before saving.",
  low: "The document was hard to read. Treat this as a starting point only.",
};

// The AI quotes the document verbatim; keep it to a readable snippet.
const snippet = text => {
  if (!text) return null;
  const clean = String(text).replace(/\s+/g, " ").trim();
  return clean.length > 220 ? `${clean.slice(0, 220)}…` : `“${clean}”`;
};

function AIPayDraftEditor({
  draft,
  state,
  onChange,
  overview,
  sellingYearId,
  presets,
}) {
  const [addTarget, setAddTarget] = useState(null);

  const patch = next => onChange({ ...state, ...next });

  const confidence = draft?.confidence || "medium";
  const warnings = draft?.warnings || [];
  const missing = draft?.missing_fields || [];

  return (
    <Stack gap="lg">
      <Card withBorder radius="md" p="md">
        <Group
          justify="space-between"
          wrap="nowrap"
          mb={warnings.length || missing.length ? "sm" : 0}
        >
          <Group gap="xs" wrap="nowrap">
            <IconSparkles size={18} color="var(--mantine-color-orange-6)" />
            <Text size="sm" fw={600}>
              Read from the document
            </Text>
          </Group>
          <Badge color={CONFIDENCE_COLOR[confidence] || "gray"} variant="light">
            {titleize(confidence)} confidence
          </Badge>
        </Group>
        <Text size="xs" c="dimmed">
          {CONFIDENCE_NOTE[confidence] || CONFIDENCE_NOTE.medium} Nothing is
          saved until you submit.
        </Text>

        {(warnings.length > 0 || missing.length > 0) && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            color="yellow"
            variant="light"
            mt="sm"
          >
            <Stack gap={4}>
              {missing.length > 0 && (
                <Text size="xs">
                  <strong>Not found in the document:</strong>{" "}
                  {missing.map(titleize).join(", ")}
                </Text>
              )}
              {warnings.length > 0 && (
                <List size="xs" spacing={2}>
                  {warnings.map((warning, i) => (
                    <List.Item key={i}>{warning}</List.Item>
                  ))}
                </List>
              )}
            </Stack>
          </Alert>
        )}
      </Card>

      <Stack gap={6}>
        <ScaleTable
          title="Commission Scale"
          col2Label="Commission"
          lines={state.commissionLines || []}
          onChange={lines => patch({ commissionLines: lines })}
          presets={presets}
        />
        {state.commissionSourceText && (
          <Text size="xs" c="dimmed" fs="italic" ta="center">
            {snippet(state.commissionSourceText)}
          </Text>
        )}
      </Stack>

      {/* One upfront pay per deal user, so the add footer goes away once set. */}
      <ItemTable
        title="Upfront Pay"
        addLabel="Add Upfront Pay"
        empty={!state.upfront}
        onAdd={state.upfront ? null : () => setAddTarget("upfront")}
        form={
          addTarget === "upfront" ? (
            <AddUpfrontPayForm
              companyUpfrontTypes={overview?.ps_company_upfront_types}
              onCreated={created => {
                patch({ upfront: created });
                setAddTarget(null);
              }}
              onCancel={() => setAddTarget(null)}
            />
          ) : null
        }
      >
        {state.upfront && (
          <ItemRow
            title={
              UPFRONT_PAY_TYPE_LABELS[state.upfront.upfront_pay_type] ||
              "Upfront Pay"
            }
            note={snippet(state.upfront._sourceText)}
            fields={upfrontFields(state.upfront, next =>
              patch({
                upfront: { ...next, _sourceText: state.upfront._sourceText },
              })
            )}
            onRemove={() => patch({ upfront: null })}
          />
        )}
      </ItemTable>

      <ItemTable
        title="Bonuses"
        addLabel="Add Bonus"
        empty={(state.bonuses || []).length === 0}
        onAdd={() => setAddTarget("bonus")}
        form={
          addTarget === "bonus" ? (
            <AddBonusForm
              inline
              sellingYearId={sellingYearId ?? null}
              companyBonusTypes={overview?.ps_company_bonus_types}
              onCreated={created => {
                patch({
                  bonuses: [
                    ...(state.bonuses || []),
                    { _id: nextEditorItemId(), ...created },
                  ],
                });
                setAddTarget(null);
              }}
              onCancel={() => setAddTarget(null)}
            />
          ) : null
        }
      >
        {(state.bonuses || []).map(bonus => (
          <ItemRow
            key={bonus._id}
            title={bonus.name || "Bonus"}
            subtitle={
              BONUS_CATEGORY_LABELS[bonus.bonus_type_category] ||
              titleize(bonus.bonus_type_category)
            }
            note={snippet(bonus._sourceText)}
            fields={bonusFields(bonus, next =>
              patch({
                bonuses: state.bonuses.map(b =>
                  b._id === bonus._id
                    ? {
                        ...next,
                        _id: bonus._id,
                        _sourceText: bonus._sourceText,
                      }
                    : b
                ),
              })
            )}
            onRemove={() =>
              patch({ bonuses: state.bonuses.filter(b => b._id !== bonus._id) })
            }
          />
        ))}
      </ItemTable>

      {/* Expenses come off the top of the whole deal, not one seller's pay. */}
      <ItemTable
        title="Expenses"
        addLabel="Add Expense"
        empty={(state.expenses || []).length === 0}
        onAdd={() => setAddTarget("expense")}
        form={
          addTarget === "expense" ? (
            <AddExpenseForm
              inline
              sellingYearId={sellingYearId ?? null}
              companyExpenseTypes={overview?.ps_company_expense_types}
              onCreated={created => {
                patch({
                  expenses: [
                    ...(state.expenses || []),
                    { _id: nextEditorItemId(), ...created },
                  ],
                });
                setAddTarget(null);
              }}
              onCancel={() => setAddTarget(null)}
            />
          ) : null
        }
      >
        {(state.expenses || []).map(expense => (
          <ItemRow
            key={expense._id}
            title={expense.name || "Expense"}
            subtitle={
              EXPENSE_CATEGORY_LABELS[expense.expense_type_category] ||
              titleize(expense.expense_type_category)
            }
            note={snippet(expense._sourceText)}
            fields={expenseFields(expense, next =>
              patch({
                expenses: state.expenses.map(e =>
                  e._id === expense._id
                    ? {
                        ...next,
                        _id: expense._id,
                        _sourceText: expense._sourceText,
                      }
                    : e
                ),
              })
            )}
            onRemove={() =>
              patch({
                expenses: state.expenses.filter(e => e._id !== expense._id),
              })
            }
          />
        ))}
      </ItemTable>

      <ReductionFactorsSection
        reductions={state.reductions || []}
        onChange={reductions => patch({ reductions })}
        makeId={nextEditorItemId}
      />
    </Stack>
  );
}

export default AIPayDraftEditor;
